/**
 * Un hallazgo, y lo que se puede hacer con él.
 *
 * Lo que decide la fila es `accion`, no de qué fuente viene. Ese es el punto de
 * todo el rediseño: antes había 7 hallazgos, 6 necesitaban que una persona
 * escribiera algo, y la pantalla no lo decía — el botón de arreglar simplemente
 * no se dibujaba, y "no hay nada que hacer" se veía igual que "no puedo hacerlo
 * yo" (Eduardo, 2026-09-06).
 *
 * ── Cada fila tiene un estado, y cada estado una salida ──────────────────────
 *
 * Lo segundo que falló (misma tarde): Evo Brain contestaba "está bien así" o
 * "no puedo decidirlo" y la fila quedaba IGUAL, con el mismo botón. Eduardo: "le
 * doy para que se apliquen y no entiendo nada, si ya se arregló o no, no cambia
 * el status". Ahora la fila dice en qué está —propuesta lista, está bien así,
 * necesita tu decisión, falló, aplicado— y en cada estado hay un botón que la
 * saca de ahí: aplicar, escribir el texto uno mismo, descartar, reintentar.
 *
 * El borrador NO se aplica solo. Se pide, se muestra con el material del que
 * salió, y se aplica si Eduardo lo aprueba — el mismo trato que los artículos, y
 * por la misma razón: lo que va a quedar publicado con el nombre del cliente lo
 * aprueba una persona.
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/Button.js';
import { Textarea } from '../../ui/Textarea.js';
import { Select } from '../../ui/Select.js';
import { Pill } from '../../PillGroup.js';
import type { PillTone } from '../../PillGroup.js';
import { useToast } from '../../ui/Toast.js';
import { useApplyEdits, useFixViolation } from '../../../hooks/useProjects.js';
import type { ManualEditOp } from '../../../hooks/useProjects.js';
import { useRedactarBorrador, useAplicarEnPagina, useDescartarHallazgo, useMarcarBorrador, useOptimizarImagenes, useAgregarCache, useArreglarFuentes, useOptimizarCarga, useAjustarContraste, useArreglarLcp } from '../../../hooks/useHallazgos.js';
import type { AccionHallazgo, Hallazgo, MaterialDelBorrador, ComoAplicar, BorradorGuardado } from '../../../hooks/useHallazgos.js';

/** El tono dice qué se puede hacer, no cuán grave es. */
const GRAVEDAD: Record<Hallazgo['gravedad'], PillTone> = {
  alta: 'danger', media: 'amber', baja: 'muted',
};

/** En qué está la fila. `inicial` es "todavía no se pidió nada". */
export type EstadoDeFila = 'inicial' | 'propuesta' | 'bien' | 'decidir' | 'error' | 'aplicado';

const TONO_ESTADO: Record<Exclude<EstadoDeFila, 'inicial'>, PillTone> = {
  propuesta: 'cyan', bien: 'cyan', decidir: 'amber', error: 'danger', aplicado: 'cyan',
};

const menor = {
  fontSize: 'var(--evo-fs-tiny)',
  color: 'var(--evo-fg-4)',
  fontFamily: 'var(--evo-font)',
} as const;

/**
 * La rejilla de la fila: gravedad · hallazgo · acción, con la acción de ancho
 * fijo para que todos los botones caigan en la misma vertical (handoff
 * paso3-1b). La cabecera de la lista usa la misma, por eso se exporta.
 */
export const REJILLA_DE_FILA = {
  display: 'grid',
  gridTemplateColumns: 'var(--evo-hallazgo-grav-w) minmax(0, 1fr) var(--evo-hallazgo-accion-w)',
  gap: 'var(--evo-space-3)',
} as const;

/**
 * Lo que una fila le ofrece al lote de arriba ("que Evo Brain redacte los N",
 * "aplicar los N"). Cada función devuelve si la cosa quedó hecha.
 */
export interface ApiDeFila {
  accion: AccionHallazgo;
  redactar: () => Promise<boolean>;
  aplicar: () => Promise<boolean>;
  tieneBorrador: () => boolean;
  /** Ya se aplicó: el lote no la vuelve a redactar. */
  aplicada: () => boolean;
  estado: () => EstadoDeFila;
}

interface Props {
  projectId: number;
  hallazgo: Hallazgo;
  /** La auditoría de accesibilidad de la que salió: lo que "arreglar" necesita. */
  auditId?: number;
  /** El lote se anota acá para poder redactar y aplicar todas las filas juntas. */
  registrar?: (id: string, api: ApiDeFila) => () => void;
  /** Avisa que la fila cambió (hay borrador, se aplicó), para recontar arriba. */
  onCambio?: () => void;
  /**
   * El borrador guardado (la revisión semanal lo dejó, o un clic anterior).
   * La fila arranca en ese estado en vez de en «inicial»: lo que Evo Brain ya
   * redactó no se vuelve a pedir ni se pierde al recargar.
   */
  guardado?: BorradorGuardado;
}

export function FilaHallazgo({ projectId, hallazgo: h, auditId, registrar, onCambio, guardado }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const redactar = useRedactarBorrador(projectId);
  const aplicarPagina = useAplicarEnPagina(projectId);
  const aplicarEdicion = useApplyEdits(projectId);
  const descartarH = useDescartarHallazgo(projectId);
  const marcarGuardado = useMarcarBorrador(projectId);
  const arreglar = useFixViolation(projectId, auditId);
  const optimizar = useOptimizarImagenes(projectId);
  const fuentes = useArreglarFuentes(projectId);
  const cache = useAgregarCache(projectId);
  const bloqueo = useOptimizarCarga(projectId, 'bloqueo');
  const video = useOptimizarCarga(projectId, 'video');
  const contraste = useAjustarContraste(projectId);
  const lcp = useArreglarLcp(projectId);

  // Arranca donde el borrador guardado quedó. `decidir` con `comoAplicar` deja
  // el campo vacío para que Eduardo escriba; sin él, no hay salida y queda
  // como aviso.
  const [estado, setEstado] = useState<EstadoDeFila>(guardado?.estado ?? 'inicial');
  const [abierto, setAbierto] = useState(false);
  const [borrador, setBorrador] = useState<string | null>(
    guardado?.estado === 'propuesta' ? guardado.borrador
      : guardado?.estado === 'decidir' && guardado.comoAplicar ? '' : null,
  );
  const [material, setMaterial] = useState<MaterialDelBorrador | null>(guardado?.material ?? null);
  const [comoAplicar, setComoAplicar] = useState<ComoAplicar>(guardado?.comoAplicar ?? null);
  const [aviso, setAviso] = useState<string | null>(guardado?.porque ?? null);

  // Lo último, para las funciones que el lote llama fuera del render.
  const ultimo = useRef({ borrador, material, comoAplicar, estado });
  ultimo.current = { borrador, material, comoAplicar, estado };

  const aplicando = aplicarPagina.isPending || aplicarEdicion.isPending;
  const fallo = (e: unknown): void => { toast.error(e instanceof Error ? e.message : t('common.error')); };

  async function pedirBorradorAsync(): Promise<boolean> {
    setAviso(null);
    try {
      const r = await redactar.mutateAsync({ check: h.regla, targetId: h.targetId, hallazgoId: h.id });
      setMaterial(r.material ?? null);
      setAbierto(true);
      if (r.sePuede && 'decorativa' in r) {
        // Está bien como está: no hay nada que aplicar, y la salida es cerrarlo.
        setBorrador(null); setComoAplicar(r.comoAplicar ?? null); setAviso(r.porque); setEstado('bien');
        return false;
      }
      if (!r.sePuede) {
        setComoAplicar(r.comoAplicar ?? null);
        setAviso(r.porque);
        if (r.fallo) { setBorrador(null); setEstado('error'); return false; }
        // Evo Brain no puede decidirlo: el campo queda vacío para que Eduardo
        // escriba él, y se aplica por el mismo camino que una propuesta.
        setBorrador(r.comoAplicar ? '' : null);
        setEstado('decidir');
        return false;
      }
      setBorrador(r.borrador);
      setComoAplicar(r.comoAplicar);
      setEstado(r.comoAplicar ? 'propuesta' : 'decidir');
      return r.comoAplicar !== null;
    } catch (e) {
      fallo(e);
      setEstado('error');
      return false;
    } finally {
      onCambio?.();
    }
  }

  function pedirBorrador(): void { void pedirBorradorAsync(); }

  async function aplicarAsync(): Promise<boolean> {
    const { borrador: texto, material: mat, comoAplicar: como } = ultimo.current;
    if (!como || texto === null || texto.trim() === '') return false;

    // Qué se resolvió, para el reporte del cliente. `antes` sale del material
    // cuando lo hay (el alt actual, el destino actual); el servidor lo completa
    // para los cambios de cabecera, que él sí conoce.
    const antes = mat?.altActual ?? mat?.piezas.find(p => /actual/.test(p.de))?.texto ?? null;
    const hallazgo = { check: h.regla, titulo: h.titulo, tipo: mat?.tipo ?? como.via, antes, despues: texto };

    try {
      if (como.via === 'pagina') {
        const r = await aplicarPagina.mutateAsync({ tipo: como.tipo, valor: texto, hallazgo, hallazgoId: h.id });
        // "No hizo falta" no es un fallo: el valor ya era ese. Decirlo evita
        // que parezca que el botón no anduvo.
        toast.success(r.cambio
          ? t('projects.design.tab.step3.health.applied')
          : t('projects.design.tab.step3.health.noChange'));
      } else {
        // El texto aprobado viaja en la operación, no el hallazgo: se aplica lo
        // que Eduardo leyó, no lo que el servidor vuelva a redactar.
        const op = { ...como.op, value: texto } as unknown as ManualEditOp;
        await aplicarEdicion.mutateAsync({ ops: [op], hallazgo });
        toast.success(t('projects.design.tab.step3.health.applied'));
        // El editor no sabe de borradores guardados: se marca aparte para que
        // al recargar la fila diga «aplicado» y no vuelva a proponer lo mismo.
        marcarGuardado.mutate({ hallazgoId: h.id, estado: 'aplicado', borrador: texto });
      }
      setAbierto(false);
      setEstado('aplicado');
      setBorrador(null);
      setComoAplicar(null);
      return true;
    } catch (e) {
      fallo(e);
      return false;
    } finally {
      onCambio?.();
    }
  }

  function aplicar(): void { void aplicarAsync(); }

  /** Cierra la fila sin tocar el archivo. El motivo queda guardado. */
  function descartar(motivo: string): void {
    descartarH.mutate({ hallazgoId: h.id, titulo: h.titulo, motivo }, {
      onError: fallo,
      onSuccess: () => { onCambio?.(); },
    });
  }

  // La fila se anota en el lote una vez; las funciones leen `ultimo`, así que
  // no importa con qué render se anotaron.
  useEffect(() => {
    if (!registrar) return;
    return registrar(h.id, {
      accion: h.accion,
      redactar: pedirBorradorAsync,
      aplicar: aplicarAsync,
      tieneBorrador: () => ultimo.current.borrador !== null && ultimo.current.borrador.trim() !== '' && ultimo.current.comoAplicar !== null,
      aplicada: () => ultimo.current.estado === 'aplicado',
      estado: () => ultimo.current.estado,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [h.id, h.accion, registrar]);

  const puedeRedactar = h.accion === 'texto';
  // "Lo arreglo yo" sin botón era una promesa vacía: la fila decía que se podía
  // y no había dónde apretar. El agente de sitios trabaja sobre la copia local
  // y el resultado se mira en Revisión; esto no publica nada.
  const puedeArreglar = h.accion === 'automatico' && h.fuente === 'accesibilidad' && auditId !== undefined && !h.arreglo;
  // El enlace al blog en el pie: un cambio de página entera, con un botón, que
  // se publica con el resto. No pasa por el modelo: no hay nada que redactar.
  const esEnlaceBlog = h.regla === 'enlace-blog';
  // La canónica: deterministo como el enlace al blog. El servidor pone la
  // dirección del sitio; no hay nada que redactar.
  const esCanonical = h.regla === 'canonical-ausente' || h.regla === 'canonical-vacia';

  function ponerCanonical(): void {
    aplicarPagina.mutate({
      tipo: 'canonical', valor: '',
      hallazgo: { check: h.regla, titulo: h.titulo, tipo: 'canonical', antes: null, despues: '' },
      hallazgoId: h.id,
    }, {
      onSuccess: r => {
        toast.success(r.cambio ? t('projects.design.tab.step3.health.applied') : t('projects.design.tab.step3.health.noChange'));
        setEstado('aplicado');
        onCambio?.();
      },
      onError: fallo,
    });
  }
  // Los arreglos de velocidad que EvoHub hace solo, cada uno con su botón:
  // imágenes y fuentes (el optimizador) y caché (el .htaccess).
  const esImagenes = h.fuente === 'velocidad' && h.accion === 'automatico' && h.arreglo === 'imagenes';
  const esFuentes = h.fuente === 'velocidad' && h.accion === 'automatico' && h.arreglo === 'fuentes';
  const esCache = h.fuente === 'velocidad' && h.accion === 'automatico' && h.arreglo === 'cache';
  const esBloqueo = h.fuente === 'velocidad' && h.accion === 'automatico' && h.arreglo === 'bloqueo';
  const esVideo = h.fuente === 'velocidad' && h.accion === 'automatico' && h.arreglo === 'video';
  // Los que EvoHub mide y corrige solo, de cualquier fuente: el contraste (color
  // real en Chromium, ajuste del editor), el hreflang (URLs absolutas), el
  // orden de los encabezados y la imagen LCP. Eran las filas sin salida.
  const esContraste = h.accion === 'automatico' && h.arreglo === 'contraste';
  const esHreflang = h.accion === 'automatico' && h.arreglo === 'hreflang';
  const esEncabezados = h.accion === 'automatico' && h.arreglo === 'encabezados';
  const esLcp = h.accion === 'automatico' && h.arreglo === 'lcp';
  const esVelocidad = esImagenes || esFuentes || esCache || esBloqueo || esVideo || esContraste || esHreflang || esEncabezados || esLcp;

  function ajustarContraste(): void {
    contraste.mutate(undefined, {
      onSuccess: r => {
        if (r.ajustados === 0) { toast.info(t('projects.design.tab.step3.health.contrastNothing', { revisados: r.revisados, sinAncla: r.sinAncla })); }
        else toast.success(t('projects.design.tab.step3.health.contrastFixed', { n: r.ajustados, portadas: r.portadas, sinAncla: r.sinAncla }));
        setEstado('aplicado');
        onCambio?.();
      },
      onError: fallo,
    });
  }

  function arreglarLcp(): void {
    lcp.mutate(undefined, {
      onSuccess: r => {
        toast.success(r.cambiado
          ? t('projects.design.tab.step3.health.lcpFixed', { url: r.medida.url ?? '' })
          : t(`projects.design.tab.step3.health.lcpNothing.${r.medida.tipo}`));
        setEstado('aplicado');
        onCambio?.();
      },
      onError: fallo,
    });
  }

  function cambioDePagina(tipo: 'hreflang' | 'encabezados'): void {
    aplicarPagina.mutate({
      tipo, valor: '',
      hallazgo: { check: h.regla, titulo: h.titulo, tipo, antes: null, despues: '' },
      hallazgoId: h.id,
    }, {
      onSuccess: r => {
        toast.success(r.cambio ? t('projects.design.tab.step3.health.applied') : t('projects.design.tab.step3.health.noChange'));
        setEstado('aplicado');
        onCambio?.();
      },
      onError: fallo,
    });
  }

  function arreglarBloqueo(): void {
    bloqueo.mutate(undefined, {
      onSuccess: r => {
        toast.success(t('projects.design.tab.step3.health.blockingFixed', { css: r.cssInlineado.length, fuentes: r.fuentes }));
        setEstado('aplicado');
        onCambio?.();
      },
      onError: fallo,
    });
  }

  function diferirVideo(): void {
    video.mutate(undefined, {
      onSuccess: r => {
        if (r.motivoVideo === 'api-de-youtube') { toast.info(t('projects.design.tab.step3.health.videoApi')); return; }
        toast.success(t('projects.design.tab.step3.health.videoDeferred', { count: r.video }));
        setEstado('aplicado');
        onCambio?.();
      },
      onError: fallo,
    });
  }

  function arreglarFuentes(): void {
    fuentes.mutate(undefined, {
      onSuccess: r => {
        toast.success(t('projects.design.tab.step3.health.fontsFixed', { lazy: r.lazy }));
        setEstado('aplicado');
        onCambio?.();
      },
      onError: fallo,
    });
  }

  function agregarCache(): void {
    cache.mutate(undefined, {
      onSuccess: r => {
        toast.success(t(r.cambiado ? 'projects.design.tab.step3.health.cacheAdded' : 'projects.design.tab.step3.health.cacheNothing'));
        setEstado('aplicado');
        onCambio?.();
      },
      onError: fallo,
    });
  }

  function optimizarImagenes(): void {
    optimizar.mutate(undefined, {
      onSuccess: r => {
        const mb = (b: number) => `${(b / 1024 / 1024).toFixed(1)} MB`;
        toast.success(r.imagenes.procesadas > 0
          ? t('projects.design.tab.step3.health.optimized', { n: r.imagenes.procesadas, antes: mb(r.imagenes.bytesAntes), despues: mb(r.imagenes.bytesDespues) })
          : t('projects.design.tab.step3.health.optimizedNothing'));
        setEstado('aplicado');
        onCambio?.();
      },
      onError: fallo,
    });
  }

  function pedirArreglo(): void {
    arreglar.mutate(h.regla, {
      onSuccess: () => { toast.success(t('projects.design.tab.step3.health.fixSent')); },
      onError: e => { toast.error(e instanceof Error ? e.message : t('common.error')); },
    });
  }

  function agregarEnlaceAlBlog(): void {
    aplicarPagina.mutate({
      tipo: 'enlaceBlog', valor: 'blog/',
      hallazgo: { check: h.regla, titulo: h.titulo, tipo: 'enlaceBlog', antes: null, despues: 'blog/' },
      hallazgoId: h.id,
    }, {
      onSuccess: r => {
        toast.success(r.cambio ? t('projects.design.tab.step3.health.applied') : t('projects.design.tab.step3.health.noChange'));
        setEstado('aplicado');
        onCambio?.();
      },
      onError: fallo,
    });
  }

  const motivoBien = aviso ?? t('projects.design.tab.step3.health.state.bien');
  const motivoNa = t('projects.design.tab.step3.health.dismissNa');
  // A dónde puede llevar el enlace. Solo lo trae el material de un destino.
  const destinos = material?.tipo === 'destino' ? (material.destinosPosibles ?? []) : [];

  // ── La columna de acción ───────────────────────────────────────────────────
  // Un botón principal, uno secundario si hay, y debajo UNA línea que dice qué
  // va a pasar. Responde "¿qué significa? ¿qué hago con eso?" en el lugar donde
  // se aprieta (Eduardo, 2026-09-06). Un hallazgo bloqueado no dibuja botón.
  const que = (clave: string) => t(`projects.design.tab.step3.health.will.${clave}`);
  const esDiagnostico = h.accion === 'diagnostico';
  const bloqueado = h.accion === 'bloqueado' || (h.accion === 'automatico' && !puedeArreglar && !esEnlaceBlog && !esCanonical && !esVelocidad);

  let accion: React.ReactNode = null;
  let consecuencia: string | null = null;
  if (esDiagnostico) {
    accion = null;
  } else if (estado === 'aplicado' && esVelocidad) {
    // Sigue en la lista porque se mide el sitio PUBLICADO: hasta publicar y
    // volver a medir, la fila dice que ya está hecho y por qué sigue acá.
    const repetir = esImagenes ? optimizarImagenes : esFuentes ? arreglarFuentes : esBloqueo ? arreglarBloqueo : esVideo ? diferirVideo
      : esContraste ? ajustarContraste : esLcp ? arreglarLcp : esHreflang ? () => { cambioDePagina('hreflang'); } : esEncabezados ? () => { cambioDePagina('encabezados'); } : agregarCache;
    const ocupado = optimizar.isPending || fuentes.isPending || cache.isPending || bloqueo.isPending || video.isPending || contraste.isPending || lcp.isPending || aplicarPagina.isPending;
    accion = (
      <>
        <Pill tone="cyan" variant="outline" size="xs">{t('projects.design.tab.step3.health.state.aplicado')}</Pill>
        <Button size="xs" variant="link" tone="muted" loading={ocupado} onClick={repetir}>{t('projects.design.tab.step3.health.repeat')}</Button>
      </>
    );
    // Sin línea debajo: «Aplicado» y «Repetir» ya lo dicen (Eduardo, 2026-09-06).
  } else if (estado === 'aplicado') {
    accion = <Pill tone="cyan" variant="outline" size="xs">{t('projects.design.tab.step3.health.state.aplicado')}</Pill>;
    consecuencia = que('workingCopy');
  } else if (esCanonical) {
    accion = (
      <>
        <Button size="xs" tone="cyan" fullWidth loading={aplicarPagina.isPending} onClick={ponerCanonical}>
          {t('projects.design.tab.step3.health.setCanonical')}
        </Button>
        <Button size="xs" variant="outline" fullWidth loading={descartarH.isPending} onClick={() => { descartar(motivoNa); }}>
          {t('projects.design.tab.step3.health.dismiss')}
        </Button>
      </>
    );
    consecuencia = que('canonical');
  } else if (esEnlaceBlog) {
    accion = (
      <>
        <Button size="xs" tone="cyan" fullWidth loading={aplicarPagina.isPending} onClick={agregarEnlaceAlBlog}>
          {t('projects.design.tab.step3.health.addBlogLink')}
        </Button>
        <Button size="xs" variant="outline" fullWidth loading={descartarH.isPending} onClick={() => { descartar(motivoNa); }}>
          {t('projects.design.tab.step3.health.dismiss')}
        </Button>
      </>
    );
    consecuencia = que('workingCopy');
  } else if (esImagenes) {
    accion = (
      <Button size="xs" tone="cyan" fullWidth loading={optimizar.isPending} onClick={optimizarImagenes}>
        {optimizar.isPending ? t('projects.design.tab.step3.health.optimizing') : t('projects.design.tab.step3.health.optimizeImages')}
      </Button>
    );
    consecuencia = que('optimize');
  } else if (esFuentes) {
    accion = (
      <Button size="xs" tone="cyan" fullWidth loading={fuentes.isPending} onClick={arreglarFuentes}>
        {t('projects.design.tab.step3.health.fixFonts')}
      </Button>
    );
    consecuencia = que('fonts');
  } else if (esCache) {
    accion = (
      <Button size="xs" tone="cyan" fullWidth loading={cache.isPending} onClick={agregarCache}>
        {t('projects.design.tab.step3.health.addCache')}
      </Button>
    );
    consecuencia = que('cache');
  } else if (esBloqueo) {
    accion = (
      <Button size="xs" tone="cyan" fullWidth loading={bloqueo.isPending} onClick={arreglarBloqueo}>
        {t('projects.design.tab.step3.health.fixBlocking')}
      </Button>
    );
    consecuencia = que('blocking');
  } else if (esVideo) {
    accion = (
      <Button size="xs" tone="cyan" fullWidth loading={video.isPending} onClick={diferirVideo}>
        {t('projects.design.tab.step3.health.deferVideo')}
      </Button>
    );
    consecuencia = que('video');
  } else if (esContraste) {
    accion = (
      <Button size="xs" tone="cyan" fullWidth loading={contraste.isPending} onClick={ajustarContraste}>
        {contraste.isPending ? t('projects.design.tab.step3.health.measuring') : t('projects.design.tab.step3.health.fixContrast')}
      </Button>
    );
    consecuencia = que('contrast');
  } else if (esHreflang) {
    accion = (
      <Button size="xs" tone="cyan" fullWidth loading={aplicarPagina.isPending} onClick={() => { cambioDePagina('hreflang'); }}>
        {t('projects.design.tab.step3.health.fixHreflang')}
      </Button>
    );
    consecuencia = que('hreflang');
  } else if (esEncabezados) {
    accion = (
      <Button size="xs" tone="cyan" fullWidth loading={aplicarPagina.isPending} onClick={() => { cambioDePagina('encabezados'); }}>
        {t('projects.design.tab.step3.health.fixHeadings')}
      </Button>
    );
    consecuencia = que('headings');
  } else if (esLcp) {
    accion = (
      <Button size="xs" tone="cyan" fullWidth loading={lcp.isPending} onClick={arreglarLcp}>
        {lcp.isPending ? t('projects.design.tab.step3.health.measuring') : t('projects.design.tab.step3.health.fixLcp')}
      </Button>
    );
    consecuencia = que('lcp');
  } else if (puedeArreglar) {
    accion = (
      <Button size="xs" variant="outline" tone="cyan" fullWidth loading={arreglar.isPending} onClick={pedirArreglo}>
        {t('projects.design.tab.step3.health.fix')}
      </Button>
    );
    consecuencia = que('agent');
  } else if (puedeRedactar && estado === 'inicial') {
    accion = (
      <Button size="xs" tone="cyan" fullWidth loading={redactar.isPending} onClick={pedirBorrador}>
        {t('projects.design.tab.step3.health.draft')}
      </Button>
    );
    consecuencia = que('evo');
  } else if (puedeRedactar && estado === 'bien') {
    accion = (
      <Button size="xs" tone="cyan" fullWidth loading={descartarH.isPending} onClick={() => { descartar(motivoBien); }}>
        {t('projects.design.tab.step3.health.dismissFine')}
      </Button>
    );
    consecuencia = que('closes');
  } else if (puedeRedactar && estado === 'error') {
    accion = (
      <>
        <Button size="xs" tone="cyan" fullWidth loading={redactar.isPending} onClick={pedirBorrador}>
          {t('projects.design.tab.step3.health.retry')}
        </Button>
        <Button size="xs" variant="outline" fullWidth loading={descartarH.isPending} onClick={() => { descartar(motivoNa); }}>
          {t('projects.design.tab.step3.health.dismissNa')}
        </Button>
      </>
    );
    consecuencia = que('evo');
  } else if (puedeRedactar && (estado === 'propuesta' || estado === 'decidir')) {
    accion = (
      <>
        <Button size="xs" tone="cyan" fullWidth loading={aplicando} disabled={borrador === null || borrador.trim() === ''} onClick={aplicar}>
          {t('projects.design.tab.step3.health.apply')}
        </Button>
        <Button size="xs" variant="outline" fullWidth loading={descartarH.isPending} onClick={() => { descartar(motivoNa); }}>
          {t('projects.design.tab.step3.health.dismissNa')}
        </Button>
      </>
    );
    consecuencia = estado === 'propuesta' ? que('youApprove') : que('youWrite');
  } else if (bloqueado) {
    // Lo que no se toca desde acá se puede cerrar desde acá: sin esto la fila
    // se quedaba para siempre (Eduardo, 2026-09-09).
    accion = (
      <Button size="xs" variant="outline" fullWidth loading={descartarH.isPending} onClick={() => { descartar(motivoNa); }}>
        {t('projects.design.tab.step3.health.dismiss')}
      </Button>
    );
    consecuencia = que('notHere');
  }

  // La meta en UNA línea: cuántos, la fuente, el detalle, la ayuda. Las pills
  // de fuente y de acción se van: la acción ya la dice el botón, y la fuente
  // es metadato, no estado.
  // Qué hace el botón va al final de la descripción, no debajo del botón
  // (Eduardo, 2026-09-07: "no necesito el texto debajo del botón").
  const meta = [
    h.cuantos > 1 ? t('projects.design.tab.step3.health.affects', { count: h.cuantos }) : null,
    t(`projects.design.tab.step3.health.source.${h.fuente}`),
    h.detalle || null,
    consecuencia,
  ].filter((x): x is string => Boolean(x));

  const desplegable = puedeRedactar && estado !== 'inicial' && estado !== 'aplicado';

  return (
    <div style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
      <div style={{ ...REJILLA_DE_FILA, alignItems: 'start', padding: 'var(--evo-space-3) 0' }}>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '2px' }}>
          <Pill tone={GRAVEDAD[h.gravedad]} variant="outline" size="xs">
            {t(`projects.design.tab.step3.health.severity.${h.gravedad}`)}
          </Pill>
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 'var(--evo-fs-body)', color: esDiagnostico || bloqueado ? 'var(--evo-fg-3)' : 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>
            {h.titulo}
          </div>
          <div style={{ ...menor, marginTop: '2px', lineHeight: 1.5 }}>
            {meta.join(' · ')}
            {h.ayudaUrl && (
              <>
                {' · '}
                <a href={h.ayudaUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--evo-cyan)', textDecoration: 'none' }}>
                  {t('projects.design.tab.step3.health.help')}
                </a>
              </>
            )}
          </div>
          {/* El motivo por el que no se toca va SIEMPRE a la vista. Una fila
              bloqueada sin razón se lee como un error de la aplicación. */}
          {h.porQueNo && (
            <div style={{ ...menor, marginTop: '2px', color: 'var(--evo-fg-3)', maxWidth: '70ch' }}>{h.porQueNo}</div>
          )}
          {/* El estado, en cuanto hay uno: propuesta lista, está bien así,
              necesita tu decisión, falló. */}
          {estado !== 'inicial' && estado !== 'aplicado' && (
            <div style={{ marginTop: 'var(--evo-space-1)', display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
              <Pill tone={TONO_ESTADO[estado]} variant="outline" size="xs">
                {t(`projects.design.tab.step3.health.state.${estado}`)}
              </Pill>
              {desplegable && (
                <Button size="xs" variant="link" tone="muted" onClick={() => { setAbierto(v => !v); }}>
                  {abierto ? t('projects.design.tab.step3.health.hide') : t('projects.design.tab.step3.health.show')}
                </Button>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)', alignItems: 'stretch' }}>
          {accion}
        </div>
      </div>

      {/* La fila abierta: el borrador y de dónde salió, en la columna del
          hallazgo, sobre una veladura para que se lea como parte de la fila. */}
      {abierto && desplegable && (
        <div style={{
          ...REJILLA_DE_FILA,
          alignItems: 'start',
          background: 'var(--evo-panel)',
          margin: '0 calc(-1 * var(--evo-space-4))',
          padding: 'var(--evo-space-3) var(--evo-space-4)',
        }}>
          <div />
          <div style={{ minWidth: 0 }}>
            {aviso && (
              <p style={{ ...menor, margin: '0 0 var(--evo-space-2)', color: estado === 'error' ? 'var(--evo-danger)' : 'var(--evo-amber)', maxWidth: '70ch', lineHeight: 1.5 }}>
                {aviso}
              </p>
            )}

            {(estado === 'propuesta' || estado === 'decidir') && borrador !== null && (
              // Un destino se ELIGE, no se escribe: la lista de a dónde puede
              // llevar el enlace ya viene con el material, y un campo vacío
              // que dice "escribe el destino" le pide a Eduardo adivinar rutas
              // que la pantalla tiene delante (2026-09-06).
              destinos.length > 0
                ? (
                  <Select
                    value={borrador === '' ? null : borrador}
                    onChange={v => { setBorrador(v ?? ''); }}
                    options={destinos.map(d => ({ value: d.ruta, label: d.titulo, description: d.ruta }))}
                    placeholder={t('projects.design.tab.step3.health.chooseDestination')}
                    label={estado === 'decidir'
                      ? t('projects.design.tab.step3.health.chooseDestination')
                      : t('projects.design.tab.step3.health.destinationLabel')}
                    size="sm"
                    searchable
                    fullWidth
                  />
                )
                : (
                  <Textarea
                    value={borrador}
                    onChange={setBorrador}
                    rows={3}
                    fullWidth
                    label={estado === 'decidir'
                      ? t('projects.design.tab.step3.health.writeYourself')
                      : t('projects.design.tab.step3.health.draftLabel')}
                  />
                )
            )}

            {/* De dónde salió el texto. Sin esto el borrador se aprueba a
                ciegas, que es lo contrario de lo que la revisión es. */}
            {material && material.piezas.length > 0 && (
              <div style={{ marginTop: 'var(--evo-space-3)' }}>
                <div style={{ ...menor, textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)' }}>
                  {t('projects.design.tab.step3.health.material')}
                </div>
                {material.piezas.map(p => (
                  <div key={p.de} style={{ ...menor, marginTop: '4px', maxWidth: '80ch' }}>
                    <span style={{ color: 'var(--evo-fg-3)' }}>{p.de}: </span>
                    {p.texto.slice(0, 200)}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div />
        </div>
      )}
    </div>
  );
}
